/**
 * S3-Compliant Sequential Update Manager
 * Ensures updates are processed sequentially without traditional debouncing
 * Follows: Stochastic input, Sequential processing, Synchronous execution
 */
class SequentialUpdateManager {
  constructor() {
    this.updateQueue = [];
    this.isProcessing = false;
    this.maxQueueSize = 10; // Fail-fast limit
  }

  async enqueueUpdate(updateFn, context = '') {
    // Fail fast if queue is overwhelmed
    if (this.updateQueue.length >= this.maxQueueSize) {
      throw new Error(`Update queue overflow (${this.updateQueue.length}/${this.maxQueueSize}) - failing fast`);
    }

    return new Promise((resolve, reject) => {
      this.updateQueue.push({
        updateFn,
        context,
        resolve,
        reject,
        timestamp: Date.now()
      });

      // Start processing if not already running
      if (!this.isProcessing) {
        this.processQueue();
      }
    });
  }

  async processQueue() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      while (this.updateQueue.length > 0) {
        const update = this.updateQueue.shift();
        
        try {
          // Sequential execution - wait for each update to complete
          const result = await update.updateFn();
          update.resolve(result);
        } catch (error) {
          // Fail fast - don't continue processing on error
          update.reject(error);
          throw error; // Stop processing queue
        }
      }
    } finally {
      this.isProcessing = false;
    }
  }

  // Clear queue on critical errors
  clearQueue() {
    this.updateQueue.forEach(update => {
      update.reject(new Error('Queue cleared due to critical error'));
    });
    this.updateQueue = [];
    this.isProcessing = false;
  }
}

export const edgeUpdateManager = new SequentialUpdateManager();
